<script setup>
import { GiftIcon, InboxStackIcon, LightBulbIcon, MegaphoneIcon, RectangleStackIcon } from '@heroicons/vue/24/outline'
import {
  ArrowsRightLeftIcon,
  CalendarIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChevronDownIcon,
  GlobeAsiaAustraliaIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  QuestionMarkCircleIcon,
  SpeakerWaveIcon,
  TagIcon,
  UserGroupIcon,
  UserIcon,
  VideoCameraIcon,
  WrenchIcon,
} from '@heroicons/vue/24/solid'
import { router } from '@inertiajs/vue3'
import { DangerBadge, Dropdown, DropdownItem } from '@codinglabsau/ui'
import { Button } from '@codinglabsau/gooey'
import hasRole from '@/Utils/hasRole.js'
import { DollarSignIcon, SolidHammerIcon, TagsIcon } from '@/Components'
import { storeMessagesTotal } from '@/Stores/messagesDropdown.js'
import CurrencyFlag from '../Icons/CurrencyFlag.vue'
import { useLocaleModal } from '@/Composables/useLocaleModal.js'

const { openLocaleModal } = useLocaleModal()
</script>

<template>
  <div class="flex">
    <Dropdown
      text-classes="border-none !shadow-none !py-0 focus:!ring-0 !px-2 mx-1"
      scroll-height="max-h-[70vh] md:max-h-[65vh]"
    >
      <template #text>
        <div class="flex items-center">
          <div class="flex flex-col items-center text-xs justify-center">
            <img
              :src="$page.props.auth.user.avatar"
              class="object-cover size-7 md:size-8 rounded-full"
              alt="avatar"
            >
            <span class="hidden md:block text-xs font-normal text-gray-600">
              {{ $page.props.auth.user.nick }}
            </span>
          </div>
          <ChevronDownIcon class="w-4 h-4 text-gray-500 hidden md:flex" />
        </div>
      </template>

      <DropdownItem
        :href="route('profile.show', $page.props.auth.user)"
        class="mx-0.5"
      >
        <div class="flex items-start gap-2">
          <div class="size-10">
            <img
              :src="$page.props.auth.user.avatar"
              :alt="__('avatar')"
              class="object-cover size-full"
            >
          </div>
          <div class="flex flex-col">
            <span class="font-bold text-sm">
              {{ $page.props.auth.user.nick }}
            </span>
            <span class="text-xs">
              {{ __('My Profile') }}
            </span>
          </div>
        </div>
      </DropdownItem>

      <div class="flex px-4 items-center gap-2 text-xs text-gray-500">
        {{ __('Currency: ') }}

        <Button
          variant="ghost"
          type="button"
          class="p-0 w-fit"
          @click="openLocaleModal()"
        >
          <CurrencyFlag />
        </Button>
      </div>

      <!-- Admin -->
      <div
        v-if="hasRole('admin')"
        class="border-b"
      >
        <div class="text-xs text-gray-400 px-4 mt-4">
          {{ __('Admin') }}
        </div>
        <DropdownItem
          :href="route('hq.index')"
          text-classes="text-xs"
        >
          <div class="flex items-center">
            <GlobeAsiaAustraliaIcon class="w-4 h-4 mr-1" />
            {{ __('HQ Dashboard') }}
          </div>
        </DropdownItem>
      </div>

      <!-- Seller -->
      <div
        v-if="hasRole('seller')"
        class="border-b"
      >
        <div class="text-xs text-gray-400 px-4 mt-4">
          {{ __('Selling') }}
        </div>
        <DropdownItem
          :href="route('seller.stores.dashboard', $page.props.auth.user.url_nick)"
        >
          <div class="flex items-center">
            <GlobeAsiaAustraliaIcon class="w-4 h-4 mr-1" />
            {{ __('Selling Dashboard') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('seller.stores.auctions.index', $page.props.auth.user.url_nick)"
        >
          <div class="flex items-center">
            <SolidHammerIcon class="w-4 h-4 mr-1" />
            {{ __('Manage Inventory') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('seller.stores.inbox.threads.index', $page.props.auth.user.url_nick)"
          external
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <ChatBubbleOvalLeftEllipsisIcon class="w-4 h-4 mr-1" />
              {{ __('Store Messages') }}
            </div>
            <DangerBadge
              v-if="storeMessagesTotal && storeMessagesTotal > 0"
              class="bg-red-500 text-white"
            >
              {{ storeMessagesTotal }}
            </DangerBadge>
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('stores.show', $page.props.auth.user.url_nick)"
          external
        >
          <div class="flex items-center">
            <TagsIcon class="w-4 h-4 mr-1" />
            {{ __('My Store') }}
          </div>
        </DropdownItem>

        <DropdownItem :href="route('seller.stores.sales.awaiting-payment', $page.props.auth.user.url_nick)">
          <div class="flex items-center">
            <DollarSignIcon class="w-4 h-4 mr-1" />
            {{ __('Sales') }}
          </div>
        </DropdownItem>

        <DropdownItem :href="route('seller.stores.offers.index', $page.props.auth.user.url_nick)">
          <div class="flex items-center">
            <ArrowsRightLeftIcon class="w-4 h-4 mr-1" />
            {{ __('Offers') }}
          </div>
        </DropdownItem>

        <DropdownItem
          v-if="$page.props.auth.stores[0] && $page.props.header.features['live-events-management']"
          :href="route('seller.stores.events.index', $page.props.auth.stores[0])"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <VideoCameraIcon class="size-4 mr-1" />
              {{ __('Live Events') }}
            </div>
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('seller.stores.coupon-campaigns.index', $page.props.auth.user.url_nick)"
        >
          <div class="flex items-center">
            <GiftIcon class="w-4 h-4 mr-1" />
            {{ __('Coupon Campaigns') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('seller.auctions.create')"
          external
        >
          <div class="flex items-center">
            <TagIcon class="w-4 h-4 mr-1" />
            {{ __('Sell an Item') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('seller.auctions.templates.index')"
          external
        >
          <div class="flex items-center">
            <InboxStackIcon class="w-4 h-4 mr-1" />
            {{ __('Templates') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('seller.stores.feedback.index', $page.props.auth.user.url_nick)"
          external
        >
          <div class="flex items-center">
            <SpeakerWaveIcon class="w-4 h-4 mr-1" />
            {{ __('Store Feedback') }}
          </div>
        </DropdownItem>
      </div>

      <!-- Sheriff -->
      <div
        v-if="hasRole('sheriff')"
        class="divide-y border-b"
      >
        <DropdownItem
          v-if="$page.props.tenant.settings['sheriff_program_enabled']"
          :href="route('hq.auction-audits.index')"
          external
        >
          <div class="flex items-center">
            <PaperClipIcon class="w-4 h-4 mr-1" />
            {{ __('Auction Audits') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('hq.auctions.index')"
        >
          <div class="flex items-center">
            <SolidHammerIcon class="w-4 h-4 mr-1" />
            {{ __('Manage Auctions') }}
          </div>
        </DropdownItem>
      </div>

      <!-- Editor -->
      <div
        v-if="hasRole('editor')"
        class="border-b"
      >
        <div class="text-xs text-gray-400 px-4 mt-4">
          {{ __('Editor') }}
        </div>
        <DropdownItem
          :href="route('hq.contents.index')"
        >
          <div class="flex items-center">
            <LightBulbIcon class="w-4 h-4 mr-1" />
            {{ __('Learn') }}
          </div>
        </DropdownItem>
      </div>

      <!-- Editor -->
      <div
        v-if="hasRole('seo')"
        class="border-b"
      >
        <div class="text-xs text-gray-400 px-4 mt-4">
          {{ __('SEO') }}
        </div>
        <DropdownItem
          :href="route('hq.categories.index')"
        >
          <div class="flex items-center">
            <RectangleStackIcon class="w-4 h-4 mr-1" />
            {{ __('Categories') }}
          </div>
        </DropdownItem>
      </div>

      <!-- Support -->
      <div
        v-if="hasRole(['support level 1', 'support level 2'])"
        class="border-b"
      >
        <DropdownItem :href="route('hq.index')">
          <div class="flex items-center">
            <GlobeAsiaAustraliaIcon class="w-4 h-4 mr-1" />
            {{ __('HQ Dashboard') }}
          </div>
        </DropdownItem>
        <DropdownItem
          :href="route('hq.auctions.index')"
        >
          <div class="flex items-center">
            <SolidHammerIcon class="w-4 h-4 mr-1" />
            {{ __('Manage Auctions') }}
          </div>
        </DropdownItem>
        <DropdownItem
          :href="route('hq.users.index')"
          external
        >
          <div class="flex items-center">
            <UserGroupIcon class="w-4 h-4 mr-1" />
            {{ __('Users') }}
          </div>
        </DropdownItem>
        <DropdownItem :href="route('hq.tickets.index')">
          <div class="flex items-center">
            <PaperAirplaneIcon class="w-4 h-4 mr-1" />
            {{ __('Support Tickets') }}
            <span
              v-if="$page.props.header.counters.support_open_tickets && $page.props.header.counters.support_open_tickets > 0"
              class="text-xs text-gray-700 ml-1 rounded-md bg-gray-300 px-1"
            >
              {{ $page.props.header.counters.support_open_tickets }}
            </span>
          </div>
        </DropdownItem>
      </div>

      <!-- Account Links -->
      <div class="border-b">
        <DropdownItem
          :href="route('account.dashboard')"
          external
        >
          <div class="flex items-center">
            <GlobeAsiaAustraliaIcon class="w-4 h-4 mr-1" />
            {{ __('Account Dashboard') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('account.offers.index')"
          external
        >
          <div class="flex items-center">
            <ArrowsRightLeftIcon class="w-4 h-4 mr-1" />
            {{ __('Offers') }}
          </div>
        </DropdownItem>

        <DropdownItem :href="route('account.feedback.index')">
          <div class="flex items-center">
            <MegaphoneIcon class="w-4 h-4 mr-1" />
            {{ __('Feedback') }}
          </div>
        </DropdownItem>

        <DropdownItem
          :href="route('account.orders.index')"
          external
        >
          <div class="flex items-center">
            <CalendarIcon class="w-4 h-4 mr-1" />
            {{ __('Order History') }}
          </div>
        </DropdownItem>

        <DropdownItem :href="route('account.settings.show')">
          <div class="flex items-center">
            <WrenchIcon class="w-4 h-4 mr-1" />
            {{ __('Settings') }}
          </div>
        </DropdownItem>
      </div>

      <!-- Support, Help and Logout -->
      <DropdownItem
        :href="route('help.index')"
        external
      >
        <div class="flex items-center">
          <QuestionMarkCircleIcon class="w-4 h-4 mr-1" />
          {{ __('Help') }}
        </div>
      </DropdownItem>
      <DropdownItem
        :href="route('account.support.tickets.index')"
      >
        <div class="flex items-center">
          <PaperAirplaneIcon class="w-4 h-4 mr-1" />
          {{ __('Support') }}
          <span
            v-if="$page.props.header.counters.open_tickets && $page.props.header.counters.open_tickets > 0"
            class="text-xs text-gray-700 ml-1 rounded-md bg-gray-300 px-1"
          >
            {{ $page.props.header.counters.open_tickets }}
          </span>
        </div>
      </DropdownItem>
      <DropdownItem
        class="w-full"
        external
        @click="router.post(route('logout'))"
      >
        <div class="flex items-center w-full">
          <UserIcon class="w-4 h-4 mr-1" />
          {{ __('Log out') }}
        </div>
      </DropdownItem>
    </Dropdown>
  </div>
</template>
